import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConfirm } from "material-ui-confirm";

import { get, post } from "../../api";
import BatteryBars from "../../components/BatteryBars";
import Button from "../../components/Button";
import CheckboxWithLabel from "../../components/CheckboxWithLabel";
import Frame from "../../components/Frame";
import FrameHeader from "../../components/FrameHeader";
import Icon from "../../components/Icon";
import Loading from "../../components/Loading";
import SerialPicker from "../../components/SerialPicker";
import TextInput from "../../components/TextInput";
import { DEVICE_TYPE_LABELS, deviceTypeLabel } from "../../constants/sentinel_types";
import {
  DEFAULT_BATCH_SIZE,
  DEFAULT_PAUSE_MS,
  EXPORT_COLUMNS,
  EXPORT_PAGE_SIZE,
  PACK_MAH,
  POLL_MS,
  RESULTS_PAGE_SIZE,
  anyRunActive,
  canCancel,
  canResume,
  deviceRunOptions,
  exportFileName,
  formatEta,
  formatMah,
  gnssSummary,
  isRunActive,
  packPercentUsed,
  pluralise,
  remainingMah,
  runCoversDevice,
  runKind,
  runLabel,
  runOptions,
  runScope,
  runTone,
  splitList,
  toExportRows,
  usageBreakdown,
} from "../../helpers/batteryUsage";
import { downloadWorkbook } from "../../helpers/excel";
import { formatDate, formatDateTime } from "../../helpers/preShipment";

import styles from "./AdminPortalBattery.module.scss";

const API = "/admin/battery-usage";
const DEVICE_TYPE_OPTIONS = Object.keys(DEVICE_TYPE_LABELS).map((id) => Number(id));

function classes(...names) {
  return names.filter((c) => c).join(" ");
}

export default function AdminPortalBattery() {
  const confirm = useConfirm();

  /* Device lookup */
  const [picked, setPicked] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  /* Runs */
  const [runs, setRuns] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsSkip, setResultsSkip] = useState(0);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  /* New run */
  const [dryRun, setDryRun] = useState(true);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [batchSize, setBatchSize] = useState("");
  const [pauseMs, setPauseMs] = useState("");

  const selected = useMemo(
    () => (runs || []).find((p) => p.run.id === selectedRunId) || null,
    [runs, selectedRunId]
  );
  const active = anyRunActive(runs);

  /* ── Loading ────────────────────────────────────────────────────────── */

  const loadRuns = useCallback(async () => {
    const res = await get(`${API}/runs`, { take: 20 });
    if (!Array.isArray(res)) {
      setError("Could not load battery runs. Try again.");
      return;
    }
    setRuns(res);
    setSelectedRunId((current) =>
      current != null && res.some((p) => p.run.id === current) ? current : res.length ? res[0].run.id : null
    );
  }, []);

  const loadResults = useCallback(async (runId, skip) => {
    setResultsLoading(true);
    const res = await get(`${API}/runs/${runId}/results`, { skip, take: RESULTS_PAGE_SIZE });
    setResultsLoading(false);
    if (res === undefined || !Array.isArray(res.results)) {
      setError("Could not load the run's results. Try again.");
      return;
    }
    setResults(res);
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  /* While something runs, its progress (and the results coming in) refresh on their own. */
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(loadRuns, POLL_MS);
    return () => clearInterval(timer);
  }, [active, loadRuns]);

  const selectedDone = selected ? selected.run.devices_done : 0;
  useEffect(() => {
    if (selectedRunId == null) {
      setResults(null);
      return;
    }
    loadResults(selectedRunId, resultsSkip);
  }, [selectedRunId, resultsSkip, selectedDone, loadResults]);

  async function pickDevice(device, { keep = false } = {}) {
    setPicked(device);
    if (!keep) setReport(null);
    setReportError(null);
    setReportLoading(!keep);
    const res = await get(`${API}/devices/${device.device_id}`);
    setReportLoading(false);
    if (res === undefined || !res.device) {
      setReportError("Could not work out this device's battery. Try again.");
      return;
    }
    setReport(res);
  }

  /**
   * A run started from the device panel is followed until it ends, then the
   * device is read again so "Saved on the device" shows what it wrote.
   */
  const followedRun = useRef(null);
  useEffect(() => {
    if (!picked || !runs || followedRun.current == null) return;
    const followed = runs.find((p) => p.run.id === followedRun.current);
    if (followed && !isRunActive(followed) && runCoversDevice(followed.run, picked.device_id)) {
      followedRun.current = null;
      pickDevice(picked, { keep: true });
    }
  }, [runs, picked]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Actions ────────────────────────────────────────────────────────── */

  const CONFIRM_BUTTONS = {
    dialogProps: { fullScreen: false },
    cancellationButtonProps: { color: "error", disableRipple: true },
    confirmationButtonProps: { color: "primary", disableRipple: true },
  };

  /** Starts a run, after asking when it writes to devices. Resolves to the new run, if any. */
  async function launch(options, confirmation) {
    if (busy || active) return;

    if (!options.dry_run) {
      try {
        await confirm({ ...confirmation, ...CONFIRM_BUTTONS });
      } catch (cancelled) {
        return;
      }
    }

    setBusy(true);
    setError(null);
    const res = await post(`${API}/runs`, options);
    setBusy(false);
    if (res === undefined || !res.run) {
      setError("The run could not be started. Another run may still be going, or an archive table has no device index.");
      return;
    }
    setResultsSkip(0);
    setSelectedRunId(res.run.id);
    loadRuns();
    return res.run;
  }

  function startRun(e) {
    e.preventDefault();
    launch(runOptions({ dryRun, deviceTypes, batchSize, pauseMs }), {
      title: "Write battery usage to devices",
      description: `Work out the charge used by every ${
        deviceTypes.length ? "device of the ticked types" : "device"
      } and save it on the device, replacing what is there. The portal shows the new bars straight away. Run a dry run first if you have not checked the figures.`,
      confirmationText: "Write to devices",
    });
  }

  async function startDeviceRun(dry) {
    if (!report) return;
    const { device, current } = report;
    const run = await launch(deviceRunOptions(device.id, dry), {
      title: `Write battery usage to ${device.serial_number}`,
      description: `Save ${formatMah(current.used_mah)} used on this device, replacing ${
        device.battery_used_mah != null ? formatMah(device.battery_used_mah) : "nothing saved yet"
      }, and make now its sync point. The portal shows the new bars straight away.`,
      confirmationText: "Write to device",
    });
    if (run) {
      followedRun.current = run.id;
      /* One device is a single batch - look again shortly rather than at the next poll. */
      setTimeout(loadRuns, 1500);
    }
  }

  /** Clears the saved figure and sync point; the portal shows the voltage reading again until a run writes it. */
  async function resetDevice() {
    if (!report || busy) return;
    const { device } = report;
    try {
      await confirm({
        title: `Reset battery of ${device.serial_number}`,
        description: `Clear the ${formatMah(device.battery_used_mah)} saved on this device and its sync point. The portal shows the voltage reading again until a run writes a new figure. Past run results are kept.`,
        confirmationText: "Reset",
        ...CONFIRM_BUTTONS,
      });
    } catch (cancelled) {
      return;
    }

    setBusy(true);
    setReportError(null);
    const res = await post(`${API}/devices/${device.id}/reset`);
    setBusy(false);
    if (res === undefined || !res.device) {
      setReportError("The device could not be reset. Try again.");
      return;
    }
    setReport(res);
  }

  async function runAction(action) {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    const res = await post(`${API}/runs/${selected.run.id}/${action}`);
    setBusy(false);
    if (res === undefined || !res.run) {
      setError(`The run could not be ${action === "cancel" ? "cancelled" : "resumed"}. Try again.`);
    }
    loadRuns();
  }

  function toggleDeviceType(id, on) {
    setDeviceTypes((current) => (on ? [...current, id].sort((a, b) => a - b) : current.filter((x) => x !== id)));
  }

  /** Every result of the selected run, a page of the API's largest size at a time. */
  async function exportExcel() {
    if (!selected || exporting) return;
    setExporting(true);
    setError(null);

    try {
      const all = [];
      for (let skip = 0; ; skip += EXPORT_PAGE_SIZE) {
        const page = await get(`${API}/runs/${selected.run.id}/results`, { skip, take: EXPORT_PAGE_SIZE });
        if (page === undefined || !Array.isArray(page.results)) throw new Error("page failed");
        all.push(...page.results);
        if (page.results.length < EXPORT_PAGE_SIZE || all.length >= page.total) break;
      }
      await downloadWorkbook({
        fileName: exportFileName(selected.run),
        sheetName: `Run ${selected.run.id}`,
        columns: EXPORT_COLUMNS,
        rows: toExportRows(all, { deviceTypeLabel }),
      });
    } catch (e) {
      setError("The spreadsheet could not be created. Try again.");
    }

    setExporting(false);
  }

  /* ── Shared pieces ──────────────────────────────────────────────────── */

  function renderPackBar(usedMah) {
    const percent = packPercentUsed(usedMah) || 0;
    const tone = percent >= 85 ? "critical" : percent >= 65 ? "low" : "ok";
    return (
      <div className={styles.packBar} title={`${percent}% of the ${PACK_MAH.toLocaleString("en-US")} mAh pack used`}>
        <div className={classes(styles.packFill, styles[`packFill_${tone}`])} style={{ width: `${percent}%` }} />
      </div>
    );
  }

  function renderBreakdown(result) {
    const parts = usageBreakdown(result);
    return (
      <div className={styles.breakdown}>
        {parts.map((part) => (
          <div key={part.key} className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>{part.label}</span>
            <div className={styles.breakdownTrack}>
              <div
                className={classes(styles.breakdownFill, styles[`breakdown_${part.key}`])}
                style={{ width: `${Math.round(part.share * 100)}%` }}
              />
            </div>
            <span className={styles.breakdownValue}>
              {formatMah(part.mah)}
              <span className={styles.muted}> · {Math.round(part.share * 100)}%</span>
            </span>
          </div>
        ))}
      </div>
    );
  }

  function renderStatus(progress) {
    return (
      <span className={classes(styles.status, styles[`status_${runTone(progress)}`])}>{runLabel(progress)}</span>
    );
  }

  /* ── Device lookup ──────────────────────────────────────────────────── */

  function renderReport() {
    if (reportLoading) {
      return (
        <div className={styles.loading}>
          <Loading color="gray" size={32} />
          <span>Reading this device's telemetry…</span>
        </div>
      );
    }
    if (reportError) return <div className={styles.error}>{reportError}</div>;
    if (!report) return null;

    const { device, current } = report;
    const stored = device.battery_used_mah;
    const sinceSync = stored != null ? current.used_mah - stored : null;

    return (
      <div className={styles.report}>
        <div className={styles.reportHead}>
          <div>
            <div className={styles.reportSerial}>
              {device.serial_number}
              <a
                className={styles.openDevice}
                href={`/devices/${device.id}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open device in a new window"
              >
                <Icon name="open_in_new" size={15} />
              </a>
            </div>
            <div className={styles.muted}>
              {device.name && device.name !== String(device.serial_number) ? `${device.name} · ` : ""}
              {deviceTypeLabel(device.devicetype_id) || "Unknown type"}
              {device.firmware ? ` · v${device.firmware}` : ""}
            </div>
          </div>
        </div>

        <div className={styles.cards}>
          <div className={styles.card}>
            <div className={styles.cardTitle}>Saved on the device</div>
            {stored != null ? (
              <>
                <div className={styles.cardFigure}>
                  <BatteryBars bars={device.battery_bars} />
                  <span>{formatMah(device.battery_remaining_mah)} left</span>
                </div>
                {renderPackBar(stored)}
                <div className={styles.cardMeta}>
                  {formatMah(stored)} used · synced {formatDateTime(device.battery_used_updated_time)}
                </div>
              </>
            ) : (
              <div className={styles.cardEmpty}>
                Not calculated yet - the portal shows the voltage reading
                {device.battery ? ` (${device.battery}%)` : ""}.
              </div>
            )}
          </div>

          <div className={styles.card}>
            <div className={styles.cardTitle}>Calculated now</div>
            {current.records > 0 ? (
              <>
                <div className={styles.cardFigure}>
                  <span>{formatMah(remainingMah(current.used_mah))} left</span>
                  <span className={styles.muted}>{packPercentUsed(current.used_mah)}% used</span>
                </div>
                {renderPackBar(current.used_mah)}
                <div className={styles.cardMeta}>
                  {formatMah(current.used_mah)} used to {formatDateTime(report.calculated_through)}
                  {sinceSync != null && ` · ${sinceSync >= 0 ? "+" : ""}${formatMah(sinceSync)} on the saved figure`}
                </div>
              </>
            ) : (
              <div className={styles.cardEmpty}>No telemetry - nothing to count from.</div>
            )}
          </div>
        </div>

        {/* Runs over this device alone: one batch, done in a moment, listed with the other runs below. */}
        <div className={styles.deviceActions}>
          <Button onClick={() => startDeviceRun(true)} disabled={busy || active}>
            Dry run this device
          </Button>
          <Button
            theme="primary"
            onClick={() => startDeviceRun(false)}
            disabled={busy || active || current.records === 0 || current.legacy_firmware}
            title={
              current.legacy_firmware
                ? "Legacy firmware - runs never write it"
                : current.records === 0
                ? "No telemetry - there is nothing to write"
                : undefined
            }
          >
            Write to this device
          </Button>
          <Button
            icon="replay"
            onClick={resetDevice}
            disabled={busy || stored == null}
            title={stored == null ? "Nothing saved on this device" : "Clear the saved figure and sync point"}
          >
            Reset
          </Button>
          {active && <span className={styles.hint}>Another run is in progress.</span>}
        </div>

        {current.records > 0 && (
          <>
            {renderBreakdown(current)}
            <div className={styles.facts}>
              <span>
                <Icon name="list" size={14} />
                {pluralise(current.records, "record")}, {pluralise(current.wakes, "wake")}
              </span>
              <span>
                <Icon name="gps_fixed" size={14} />
                {gnssSummary(current)}
              </span>
              <span>
                <Icon name="schedule" size={14} />
                {formatDate(current.first_time)} – {formatDate(current.last_time)}
              </span>
            </div>
          </>
        )}

        {current.legacy_firmware && (
          <div className={styles.warning}>
            <Icon name="warning" size={16} />
            Legacy firmware (v{device.firmware}, the old SDK). It never reports how long GPS ran, so these figures
            are a rough guess and runs never write them - the device stays on the voltage reading.
          </div>
        )}

        {report.skipped_tables && (
          <div className={styles.warning}>
            <Icon name="warning" size={16} />
            Not counted: {splitList(report.skipped_tables).join(", ")} - no device_id index, so reading it would scan the whole table.
          </div>
        )}

        {report.history && report.history.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Run</th>
                <th>Kind</th>
                <th>Counted to</th>
                <th className={styles.num}>Used</th>
                <th>Written</th>
              </tr>
            </thead>
            <tbody>
              {report.history.map((h) => (
                <tr key={h.run_id}>
                  <td>#{h.run_id}</td>
                  <td>{runKind(h)}</td>
                  <td>{formatDateTime(h.through_time)}</td>
                  <td className={styles.num}>{formatMah(h.result.used_mah)}</td>
                  <td>{h.result.applied ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  /* ── Runs ───────────────────────────────────────────────────────────── */

  function renderStartForm() {
    return (
      <form onSubmit={startRun}>
        <p className={styles.intro}>
          A run over <strong>every device</strong> (or every device of the ticked types) works out
          the charge each has used from its whole telemetry history, a few devices at a time with
          a pause in between so the database is never held for long. A dry run only records the
          figures; write to devices once they look right. Writing also sets the sync point the
          weekly update carries on from. For a single device, look it up above and use its buttons.
        </p>

        <div className={styles.formRow}>
          <CheckboxWithLabel
            fullwidth
            label="Dry run - record the figures, change no device"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            disabled={busy}
          />
        </div>

        <div className={styles.formLabel}>Device types {deviceTypes.length === 0 && <span className={styles.muted}>(none ticked = all)</span>}</div>
        <div className={styles.typeGrid}>
          {DEVICE_TYPE_OPTIONS.map((id) => (
            <CheckboxWithLabel
              key={id}
              label={deviceTypeLabel(id)}
              checked={deviceTypes.includes(id)}
              onChange={(e) => toggleDeviceType(id, e.target.checked)}
              disabled={busy}
            />
          ))}
        </div>

        <div className={styles.tuning}>
          <label className={styles.tuningField}>
            <span className={styles.formLabel}>Devices per batch</span>
            <TextInput
              type="number"
              min={1}
              max={200}
              value={batchSize}
              placeholder={String(DEFAULT_BATCH_SIZE)}
              onChange={(e) => setBatchSize(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className={styles.tuningField}>
            <span className={styles.formLabel}>Pause between batches (ms)</span>
            <TextInput
              type="number"
              min={0}
              value={pauseMs}
              placeholder={String(DEFAULT_PAUSE_MS)}
              onChange={(e) => setPauseMs(e.target.value)}
              disabled={busy}
            />
          </label>
          <div className={styles.tuningAction}>
            <Button theme="primary" type="submit" disabled={busy || active || runs === null} isLoading={busy}>
              {dryRun ? "Dry run all devices" : "Write to all devices"}
            </Button>
          </div>
        </div>
        {active && <div className={styles.hint}>A run is in progress - start another once it ends or is cancelled.</div>}
      </form>
    );
  }

  function renderRunList() {
    if (runs === null) {
      return (
        <div className={styles.loading}>
          <Loading color="gray" size={32} />
        </div>
      );
    }
    if (runs.length === 0) return <div className={styles.empty}>No runs yet.</div>;

    return (
      <table className={classes(styles.table, styles.runTable)}>
        <thead>
          <tr>
            <th>Run</th>
            <th>Kind</th>
            <th>Status</th>
            <th className={styles.num}>Progress</th>
            <th>Covers</th>
            <th>Counted to</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((p) => (
            <tr
              key={p.run.id}
              className={p.run.id === selectedRunId ? styles.selectedRow : ""}
              onClick={() => {
                setSelectedRunId(p.run.id);
                setResultsSkip(0);
              }}
            >
              <td>#{p.run.id}</td>
              <td>{runKind(p.run)}</td>
              <td>{renderStatus(p)}</td>
              <td className={styles.num}>
                {p.percent_done}% · {p.run.devices_done.toLocaleString("en-US")}/{p.run.devices_total.toLocaleString("en-US")}
              </td>
              <td>{runScope(p.run, deviceTypeLabel)}</td>
              <td>{formatDateTime(p.run.through_time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderRunDetail() {
    if (!selected) return null;
    const { run, stats } = selected;

    return (
      <>
        <div className={styles.sectionHeader}>
          <FrameHeader title={`Run #${run.id} · ${runKind(run)}`}>
            <div className={styles.headerActions}>
              {canCancel(selected) && (
                <Button onClick={() => runAction("cancel")} disabled={busy}>
                  Cancel
                </Button>
              )}
              {canResume(selected) && (
                <Button onClick={() => runAction("resume")} disabled={busy || active}>
                  Resume
                </Button>
              )}
              <Button
                icon="file_download"
                onClick={exportExcel}
                disabled={exporting || !stats || stats.devices === 0}
                isLoading={exporting}
              >
                Export to Excel
              </Button>
            </div>
          </FrameHeader>
        </div>

        <Frame>
          <div className={styles.progressHead}>
            {renderStatus(selected)}
            <span>
              {run.devices_done.toLocaleString("en-US")} of {pluralise(run.devices_total, "device")}
            </span>
            {isRunActive(selected) && <span className={styles.muted}>{formatEta(selected.eta_seconds)}</span>}
          </div>
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${selected.percent_done}%` }} />
          </div>
          <div className={styles.facts}>
            <span>{pluralise(run.rows_read, "telemetry row")} read</span>
            <span>Last batch {run.last_batch_ms.toLocaleString("en-US")} ms</span>
            <span>
              {run.batch_size} per batch, {run.pause_ms} ms pause
            </span>
            <span>Counted to {formatDateTime(run.through_time)}</span>
            <span>Tables: {splitList(run.tables).join(", ") || "none"}</span>
          </div>

          {selected.stale && (
            <div className={styles.warning}>
              <Icon name="warning" size={16} />
              Nothing has moved for a while - the server most likely restarted. Resume carries on from device {run.cursor_device_id}.
            </div>
          )}
          {run.skipped_tables && (
            <div className={styles.warning}>
              <Icon name="warning" size={16} />
              Not counted: {splitList(run.skipped_tables).join(", ")} - no device_id index. These figures are short by whatever
              those tables hold, so this run can only be a dry run.
            </div>
          )}
          {run.error && (
            <div className={styles.error}>
              <Icon name="error_outline" size={16} />
              {run.error}
            </div>
          )}

          {stats && stats.devices > 0 && (
            <div className={styles.tiles}>
              <div className={styles.tile}>
                <span className={styles.tileValue}>{stats.with_telemetry.toLocaleString("en-US")}</span>
                <span className={styles.tileLabel}>with telemetry of {stats.devices.toLocaleString("en-US")}</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileValue}>{formatMah(stats.avg_used_mah)}</span>
                <span className={styles.tileLabel}>average used</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileValue}>{formatMah(stats.max_used_mah)}</span>
                <span className={styles.tileLabel}>most used</span>
              </div>
              <div className={classes(styles.tile, stats.over_capacity > 0 ? styles.tileWarn : "")}>
                <span className={styles.tileValue}>{stats.over_capacity.toLocaleString("en-US")}</span>
                <span className={styles.tileLabel}>past the whole pack</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileValue}>
                  {stats.gnss_fixes + stats.gnss_timeouts > 0
                    ? `${Math.round((100 * stats.gnss_timeouts) / (stats.gnss_fixes + stats.gnss_timeouts))}%`
                    : "—"}
                </span>
                <span className={styles.tileLabel}>GPS searches without a record</span>
              </div>
            </div>
          )}
        </Frame>

        {renderResults()}
      </>
    );
  }

  function renderResults() {
    if (!results) return null;
    const from = results.total === 0 ? 0 : resultsSkip + 1;
    const to = Math.min(results.total, resultsSkip + RESULTS_PAGE_SIZE);

    return (
      <Frame padding={0}>
        {results.total === 0 ? (
          <div className={styles.empty}>{resultsLoading ? "Loading…" : "No devices done yet."}</div>
        ) : (
          <>
            <div className={styles.tableScroll}>
              <table className={classes(styles.table, styles.resultsTable)}>
                <thead>
                  <tr>
                    <th>Serial</th>
                    <th>Type</th>
                    <th>Used</th>
                    <th className={styles.num}>GPS</th>
                    <th className={styles.num}>Sessions</th>
                    <th className={styles.num}>Sleep</th>
                    <th className={styles.num}>Wakes</th>
                    <th className={styles.num} title="GPS searches without a GPS record, of all searches">GPS no record</th>
                    <th>Last telemetry</th>
                    <th className={styles.num}>Before</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((r) => (
                    <tr key={r.id} className={r.records === 0 ? styles.quietRow : ""}>
                      <td>
                        <button
                          type="button"
                          className={styles.linkButton}
                          onClick={() => pickDevice({ device_id: r.device_id, serial_number: r.serial_number })}
                          title="Show this device's battery above"
                        >
                          {r.serial_number != null ? r.serial_number : `#${r.device_id}`}
                        </button>
                      </td>
                      <td>
                        {deviceTypeLabel(r.devicetype_id)}
                        {r.firmware && <span className={styles.muted}> · v{r.firmware}</span>}
                        {r.legacy_firmware && (
                          <span className={styles.legacyFlag} title="Old-SDK firmware - worked out but never written">
                            legacy
                          </span>
                        )}
                      </td>
                      <td className={styles.usedCell}>
                        <span>{r.records === 0 ? "No telemetry" : formatMah(r.used_mah)}</span>
                        {r.records > 0 && renderPackBar(r.used_mah)}
                      </td>
                      <td className={styles.num}>{formatMah(r.gnss_mah)}</td>
                      <td className={styles.num}>{formatMah(r.session_mah)}</td>
                      <td className={styles.num}>{formatMah(r.sleep_mah)}</td>
                      <td className={styles.num}>{r.wakes.toLocaleString("en-US")}</td>
                      <td className={styles.num}>
                        {r.gnss_timeouts.toLocaleString("en-US")}
                        <span className={styles.muted}> / {(r.gnss_fixes + r.gnss_timeouts).toLocaleString("en-US")}</span>
                      </td>
                      <td>{formatDate(r.last_time)}</td>
                      <td className={styles.num}>{formatMah(r.previous_used_mah)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.pager}>
              <span className={styles.muted}>
                {from.toLocaleString("en-US")}–{to.toLocaleString("en-US")} of {results.total.toLocaleString("en-US")}, most used first
              </span>
              <div className={styles.pagerButtons}>
                {resultsLoading && <Loading color="gray" size={20} />}
                <Button
                  disabled={resultsSkip === 0 || resultsLoading}
                  onClick={() => setResultsSkip(Math.max(0, resultsSkip - RESULTS_PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  disabled={to >= results.total || resultsLoading}
                  onClick={() => setResultsSkip(resultsSkip + RESULTS_PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Frame>
    );
  }

  /* ── Page ───────────────────────────────────────────────────────────── */

  return (
    <>
      <FrameHeader title="Device battery" />
      <Frame>
        <p className={styles.intro}>
          Find a device to see the charge saved on it beside what its telemetry adds up to right
          now. Nothing is saved from here.
        </p>
        <SerialPicker
          selected={picked ? [String(picked.serial_number)] : []}
          onPick={pickDevice}
          placeholder="Find a device by the last digits of its serial…"
        />
        {renderReport()}
      </Frame>

      <div className={styles.sectionHeader}>
        <FrameHeader title="Battery runs" />
      </div>
      <Frame>{renderStartForm()}</Frame>

      {error && <div className={styles.error}>{error}</div>}

      <Frame padding={0}>{renderRunList()}</Frame>

      {renderRunDetail()}
    </>
  );
}
